import { item, track } from '../../features/analytics/analytics';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@skytech/shared';

import { ProductImagePreview } from './ProductImagePreview';
import { Price } from './Price';
import { Rating } from './Rating';
import { StockBadge } from './Badge';
import { InlineSpinner } from './Spinner';
import { useCart } from '../../features/cart/store';
import { WishlistButton } from '../../features/wishlist/WishlistButton';

/**
 * Catalog / carousel product card (SRS §4.3): optimized thumbnail, name, price,
 * stock, rating and independent purchase controls. Buttons remain siblings of links.
 */
export function ProductCard({
  product,
  priority = false,
  largeMobileImage = false,
  listId = 'catalog',
}: {
  product: Product;
  priority?: boolean;
  largeMobileImage?: boolean;
  listId?: string;
}): JSX.Element {
  const addToCart = useCart((s) => s.add);
  const [adding, setAdding] = useState(false);
  const out = product.stockQuantity <= 0;

  async function handleAdd(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (adding) return;
    setAdding(true);
    try {
      await addToCart(product, 1);
    } finally {
      setAdding(false);
    }
  }
  const discount =
    product.compareAtPrice && Number(product.compareAtPrice) > Number(product.price)
      ? Math.round((1 - Number(product.price) / Number(product.compareAtPrice)) * 100)
      : null;

  return (
    <article className="product-card @container group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-frost bg-white shadow-card">
      <div className="relative">
        <ProductImagePreview
          product={product}
          priority={priority}
          mobileSize={largeMobileImage ? 'medium' : undefined}
          listId={listId}
        />
        {discount && (
          <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-green-700 px-2 py-0.5 text-[11px] font-medium text-white">
            -{discount}%
          </span>
        )}
        <WishlistButton
          productId={product.id}
          className="absolute right-2 top-2 z-10 h-11 w-11 bg-white/95 shadow-card"
        />
      </div>

      <div className="flex flex-1 flex-col p-3 sm:p-4">
        <div className="flex min-h-5 items-center justify-between gap-1">
          <StockBadge stock={product.stockQuantity} threshold={product.lowStockThreshold} compact />
          {product.reviewCount > 0 && (
            <Rating value={product.averageRating} count={product.reviewCount} compact />
          )}
        </div>
        <h3 className="mt-2 min-h-10 text-sm font-normal leading-snug text-navy-900">
          <Link
            onClick={() => track('select_item', { item_list_id: listId, items: [item(product)] })}
            to={`/products/${product.slug}`}
            className="line-clamp-2 hover:text-sky-ink hover:underline"
          >
            {product.name}
          </Link>
        </h3>
        <div className="mt-auto pt-3">
          <div className="min-h-11 @[240px]:min-h-6">
            <Price
              amount={product.price}
              compareAt={product.compareAtPrice}
              compact
              className="w-full text-sm tabular-nums @[160px]:text-base"
            />
          </div>
          <button
            type="button"
            disabled={out || adding}
            onClick={(e) => void handleAdd(e)}
            aria-busy={adding || undefined}
            className="mt-3 inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white transition-shadow hover:shadow-hover disabled:opacity-40"
            aria-label={`Add ${product.name} to cart`}
          >
            {adding && <InlineSpinner className="h-3.5 w-3.5" />}
            {out ? 'Sold out' : adding ? 'Adding…' : 'Add to cart'}
          </button>
        </div>
      </div>
    </article>
  );
}
